import { type ReactNode, useId } from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import classNames from 'classnames';

import { useTooltipOpen } from './useTooltipOpen';

import styles from './Tooltip.module.scss';

export type TooltipPlacement =
    | 'top'
    | 'top-start'
    | 'top-end'
    | 'bottom'
    | 'bottom-start'
    | 'bottom-end'
    | 'left'
    | 'right';

/**
 * How the hover target sits in its parent. `fill` stretches to the parent's width, which
 * the card consumers rely on; `fit` shrinks to the child so the arrow points at it;
 * `inline` keeps the target in a line of text.
 */
export type TooltipTargetLayout = 'fill' | 'fit' | 'inline';

export type TooltipDelay = number | { show: number; hide: number };

export interface TooltipProps {
    children: ReactNode;
    content: ReactNode;
    /** Bold first line above the content. */
    heading?: ReactNode;
    placement?: TooltipPlacement;
    disabled?: boolean;
    delay?: TooltipDelay;
    maxWidth?: number;
    layout?: TooltipTargetLayout;
    /** Class on the hover target wrapper. */
    targetClassName?: string;
    /** Class on the bubble. */
    bubbleClassName?: string;
}

const LAYOUT_CLASS: Record<TooltipTargetLayout, string> = {
    fill: styles.layoutFill,
    fit: styles.layoutFitContent,
    inline: styles.layoutInline,
};

type Side = 'top' | 'bottom' | 'left' | 'right';
type Align = 'start' | 'center' | 'end';

const POSITION: Record<TooltipPlacement, [Side, Align]> = {
    top: ['top', 'center'],
    'top-start': ['top', 'start'],
    'top-end': ['top', 'end'],
    bottom: ['bottom', 'center'],
    'bottom-start': ['bottom', 'start'],
    'bottom-end': ['bottom', 'end'],
    left: ['left', 'center'],
    right: ['right', 'center'],
};

/** The arrow's size. Radix keeps the bubble an arrow's height off the target, as it was. */
const ARROW_WIDTH = 12.8;
const ARROW_HEIGHT = 6.4;
/** The room kept between the bubble and the window's edge. */
const COLLISION_PADDING = 5;

function isEmptyContent(content: ReactNode): boolean {
    return content === undefined || content === null || content === false || content === '';
}

/**
 * A string tooltip written with line breaks, as several translations are, shows one line per
 * break; the bubble would otherwise collapse them into spaces.
 */
function withLineBreaks(content: ReactNode): ReactNode {
    if (typeof content !== 'string' || !content.includes('\n')) {
        return content;
    }

    // Lines can repeat (a blank one, say), so each key counts the copies before it.
    const seen = new Map<string, number>();
    return content.split('\n').map((line) => {
        const copy = (seen.get(line) ?? 0) + 1;
        seen.set(line, copy);
        return <div key={`${line}#${copy}`}>{line}</div>;
    });
}

/**
 * The one tooltip in the portal, on Radix's Tooltip. The bubble is portalled out of
 * clipping containers and flips to the side that fits. It opens on hover, on keyboard
 * focus and on a tap; Escape, a click on the target or scrolling it away closes it. Only
 * one shows at a time.
 */
export function Tooltip(props: TooltipProps) {
    const {
        children,
        content,
        heading,
        placement = 'top',
        disabled = false,
        delay = 0,
        maxWidth = 350,
        layout = 'fill',
        targetClassName,
        bubbleClassName,
    } = props;

    const showDelay = typeof delay === 'number' ? delay : delay.show;
    const hideDelay = typeof delay === 'number' ? delay : delay.hide;
    const { open, onOpenChange, triggerHandlers } = useTooltipOpen(hideDelay);
    // Radix's own ids hold colons, which jsdom's selector engine chokes on in tests.
    const bubbleId = `tooltip-${useId().replace(/:/g, '')}`;

    if (disabled || isEmptyContent(content)) {
        return <>{children}</>;
    }

    const TargetTag = layout === 'inline' ? 'span' : 'div';
    const [side, align] = POSITION[placement];

    return (
        // A provider per tooltip: each keeps its own delay, and the bubble cannot be hovered,
        // so it never covers what the pointer moves on to.
        <TooltipPrimitive.Provider
            delayDuration={showDelay}
            skipDelayDuration={0}
            disableHoverableContent>
            <TooltipPrimitive.Root open={open} onOpenChange={onOpenChange}>
                <TooltipPrimitive.Trigger asChild {...triggerHandlers}>
                    <TargetTag
                        data-tooltip-target=""
                        className={classNames(LAYOUT_CLASS[layout], targetClassName)}>
                        {children}
                    </TargetTag>
                </TooltipPrimitive.Trigger>
                <TooltipPrimitive.Portal>
                    {/* The bubble is our own element so it carries our id from its first
                        render; Radix would give it a colon id until an effect swaps it. */}
                    <TooltipPrimitive.Content
                        asChild
                        id={bubbleId}
                        side={side}
                        align={align}
                        collisionPadding={COLLISION_PADDING}>
                        <div
                            id={bubbleId}
                            className={classNames(styles.bubble, bubbleClassName)}
                            style={{ maxWidth: `${maxWidth}px` }}>
                            {heading ? <div className={styles.heading}>{heading}</div> : null}
                            {withLineBreaks(content)}
                            <TooltipPrimitive.Arrow
                                className={styles.arrow}
                                width={ARROW_WIDTH}
                                height={ARROW_HEIGHT}
                            />
                        </div>
                    </TooltipPrimitive.Content>
                </TooltipPrimitive.Portal>
            </TooltipPrimitive.Root>
        </TooltipPrimitive.Provider>
    );
}

export default Tooltip;
